import React, { useState } from 'react';
import SEO from '../components/SEO';
import PageHeader from '../components/PageHeader';

export default function FreightRates() {
  const [formData, setFormData] = useState({
    countryFrom: '',
    cityFrom: '',
    zipFrom: '',
    countryTo: '',
    cityTo: '',
    zipTo: '',
    goodsDesc: '',
    goodsValue: '',
    length: '',
    width: '',
    height: '',
    weight: '',
    name: '',
    phone: '',
    email: '',
    comments: '',
    senderType: 'Firma',
    receiverType: 'Firma',
    buyerType: 'Firma',
    senderCompany: '',
    senderVat: '',
    receiverCompany: '',
    receiverVat: '',
    buyerCompany: '',
    buyerVat: '',
    website_url: '' // honeypot
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState(null);
  const [serverMessage, setServerMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const validate = () => {
    const errs = {};
    if (!formData.countryFrom.trim()) errs.countryFrom = 'Feltet er påkrævet';
    if (!formData.cityFrom.trim()) errs.cityFrom = 'Feltet er påkrævet';
    if (!formData.countryTo.trim()) errs.countryTo = 'Feltet er påkrævet';
    if (!formData.cityTo.trim()) errs.cityTo = 'Feltet er påkrævet';
    if (!formData.goodsDesc.trim()) errs.goodsDesc = 'Feltet er påkrævet';
    if (!formData.name.trim()) errs.name = 'Venligst indtast dit navn';
    if (!formData.phone.trim()) errs.phone = 'Venligst indtast telefonnummer';
    if (!formData.email.trim()) {
      errs.email = 'Venligst indtast e-mailadresse';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      errs.email = 'Ugyldig e-mailadresse';
    }
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      window.scrollTo({ top: 400, behavior: 'smooth' });
      return;
    }

    setIsSubmitting(true);
    setStatus(null);
    setServerMessage('');

    try {
      const response = await fetch('/_api/webform/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'Fragtforespørgsel',
          fra_land: formData.countryFrom,
          fra_by: formData.cityFrom,
          fra_postnr: formData.zipFrom,
          til_land: formData.countryTo,
          til_by: formData.cityTo,
          til_postnr: formData.zipTo,
          gods_beskrivelse: formData.goodsDesc,
          gods_vaerdi: formData.goodsValue,
          maal_vaegt: `L: ${formData.length} cm, B: ${formData.width} cm, H: ${formData.height} cm, Vægt: ${formData.weight} kg`,
          kontakt_navn: formData.name,
          kontakt_telefon: formData.phone,
          kontakt_email: formData.email,
          kommentarer: formData.comments,
          parter: `Afsender: ${formData.senderType} (${formData.senderCompany || ''}), Modtager: ${formData.receiverType} (${formData.receiverCompany || ''}), Køber: ${formData.buyerType} (${formData.buyerCompany || ''})`,
          website_url: formData.website_url
        })
      });

      if (response.ok) {
        setStatus('success');
      } else {
        const errorData = await response.json().catch(() => ({}));
        setStatus('error');
        setServerMessage(errorData.message || 'Der opstod en fejl under afsendelsen. Venligst prøv igen.');
      }
    } catch (err) {
      setStatus('error');
      setServerMessage('Netværksfejl under afsendelse. Venligst kontakt os direkte på +45 76 12 12 30.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEO
        title="Konkurrencedygtige fragtpriser - Fastlane ApS"
        description="Fastlane ApS tilbyder gode fragtrater på soefragt, luftfragt, kurértransport og vejtransport. Indsend en uforpligtende forespørgsel her."
        canonicalUrl="https://fastlane.dk/fragtrater"
      />

      <PageHeader
        title="Forespørgsel Fragtpris"
        subtitle="Udfyld formularen for et præcist og uforpligtende tilbud på din fragt i Europa eller oversøisk."
        breadcrumbs={[{ label: 'Forespørgsel Fragtpris' }]}
      />

      <div className="py-12 sm:py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200/90 p-6 sm:p-10">
            {status === 'success' ? (
              <div className="p-8 text-center bg-emerald-50 rounded-xl border border-emerald-200">
                <i className="fa-solid fa-circle-check text-4xl text-emerald-600 mb-3 block" aria-hidden="true" />
                <h3 className="text-xl font-bold text-emerald-900 mb-2">Mange tak for din forespørgsel!</h3>
                <p className="text-sm text-emerald-800 leading-relaxed max-w-lg mx-auto mb-6">
                  Vi gennemgår dine transportoplysninger og vender tilbage med en konkurrencedygtig fragtrate snarest muligt.
                </p>
                <button
                  type="button"
                  onClick={() => setStatus(null)}
                  className="px-5 py-2.5 bg-[#0f2b48] text-white text-xs font-semibold rounded-lg hover:bg-sky-900 transition-colors"
                >
                  Send en ny forespørgsel
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-8" noValidate>
                {/* Honeypot */}
                <input
                  name="website_url"
                  type="text"
                  value={formData.website_url}
                  onChange={handleChange}
                  tabIndex={-1}
                  autoComplete="off"
                  className="sr-only absolute -left-[9999px]"
                  aria-hidden="true"
                />

                {status === 'error' && (
                  <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-red-900 flex items-start gap-3">
                    <i className="fa-solid fa-circle-exclamation text-red-600 text-lg mt-0.5" aria-hidden="true" />
                    <div>
                      <h4 className="font-bold text-sm">Der opstod en fejl</h4>
                      <p className="text-xs text-red-800">{serverMessage}</p>
                    </div>
                  </div>
                )}

                {/* Sektion 1: Fra & Til */}
                <div>
                  <h3 className="text-lg font-bold text-[#0f2b48] pb-2 border-b border-slate-100 flex items-center gap-2 mb-4">
                    <i className="fa-solid fa-route text-sky-600" aria-hidden="true" />
                    <span>Rute: Afhentning og Levering</span>
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Fra */}
                    <div className="space-y-3 bg-slate-50/60 p-4 rounded-xl border border-slate-100">
                      <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700">Fra (Afhentningssted)</h4>
                      <div>
                        <label className="block text-xs font-semibold text-slate-700 mb-1">Land (fra) *</label>
                        <input
                          type="text"
                          name="countryFrom"
                          value={formData.countryFrom}
                          onChange={handleChange}
                          placeholder="F.eks. Danmark, Kina, Tyskland"
                          className="w-full px-3 py-2 text-sm border rounded-lg bg-white border-slate-300 focus:outline-hidden focus:ring-2 focus:ring-sky-600"
                        />
                        {errors.countryFrom && <p className="text-xs text-red-600 mt-1">{errors.countryFrom}</p>}
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-xs font-semibold text-slate-700 mb-1">By (fra) *</label>
                          <input
                            type="text"
                            name="cityFrom"
                            value={formData.cityFrom}
                            onChange={handleChange}
                            placeholder="By"
                            className="w-full px-3 py-2 text-sm border rounded-lg bg-white border-slate-300 focus:outline-hidden focus:ring-2 focus:ring-sky-600"
                          />
                          {errors.cityFrom && <p className="text-xs text-red-600 mt-1">{errors.cityFrom}</p>}
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-slate-700 mb-1">Postnummer (fra)</label>
                          <input
                            type="text"
                            name="zipFrom"
                            value={formData.zipFrom}
                            onChange={handleChange}
                            placeholder="Postnr."
                            className="w-full px-3 py-2 text-sm border rounded-lg bg-white border-slate-300 focus:outline-hidden focus:ring-2 focus:ring-sky-600"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Til */}
                    <div className="space-y-3 bg-slate-50/60 p-4 rounded-xl border border-slate-100">
                      <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700">Til (Leveringssted)</h4>
                      <div>
                        <label className="block text-xs font-semibold text-slate-700 mb-1">Land (til) *</label>
                        <input
                          type="text"
                          name="countryTo"
                          value={formData.countryTo}
                          onChange={handleChange}
                          placeholder="F.eks. Sverige, USA, Frankrig"
                          className="w-full px-3 py-2 text-sm border rounded-lg bg-white border-slate-300 focus:outline-hidden focus:ring-2 focus:ring-sky-600"
                        />
                        {errors.countryTo && <p className="text-xs text-red-600 mt-1">{errors.countryTo}</p>}
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-xs font-semibold text-slate-700 mb-1">By (til) *</label>
                          <input
                            type="text"
                            name="cityTo"
                            value={formData.cityTo}
                            onChange={handleChange}
                            placeholder="By"
                            className="w-full px-3 py-2 text-sm border rounded-lg bg-white border-slate-300 focus:outline-hidden focus:ring-2 focus:ring-sky-600"
                          />
                          {errors.cityTo && <p className="text-xs text-red-600 mt-1">{errors.cityTo}</p>}
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-slate-700 mb-1">Postnummer (til)</label>
                          <input
                            type="text"
                            name="zipTo"
                            value={formData.zipTo}
                            onChange={handleChange}
                            placeholder="Postnr."
                            className="w-full px-3 py-2 text-sm border rounded-lg bg-white border-slate-300 focus:outline-hidden focus:ring-2 focus:ring-sky-600"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Sektion 2: Godstype og Dimensioner */}
                <div>
                  <h3 className="text-lg font-bold text-[#0f2b48] pb-2 border-b border-slate-100 flex items-center gap-2 mb-4">
                    <i className="fa-solid fa-boxes-stacked text-sky-600" aria-hidden="true" />
                    <span>Godstype & Dimensioner</span>
                  </h3>

                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-slate-700 mb-1">Kort beskrivelse af godset *</label>
                        <input
                          type="text"
                          name="goodsDesc"
                          value={formData.goodsDesc}
                          onChange={handleChange}
                          placeholder="F.eks. Pallegods, maskindele, elektronik"
                          className="w-full px-3 py-2 text-sm border rounded-lg bg-white border-slate-300 focus:outline-hidden focus:ring-2 focus:ring-sky-600"
                        />
                        {errors.goodsDesc && <p className="text-xs text-red-600 mt-1">{errors.goodsDesc}</p>}
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate-700 mb-1">Godsværdi (valuta/beløb)</label>
                        <input
                          type="text"
                          name="goodsValue"
                          value={formData.goodsValue}
                          onChange={handleChange}
                          placeholder="F.eks. 50.000 DKK"
                          className="w-full px-3 py-2 text-sm border rounded-lg bg-white border-slate-300 focus:outline-hidden focus:ring-2 focus:ring-sky-600"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-slate-700 mb-1">Længde (cm)</label>
                        <input
                          type="number"
                          name="length"
                          value={formData.length}
                          onChange={handleChange}
                          placeholder="120"
                          className="w-full px-3 py-2 text-sm border rounded-lg bg-white border-slate-300 focus:outline-hidden focus:ring-2 focus:ring-sky-600"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate-700 mb-1">Bredde (cm)</label>
                        <input
                          type="number"
                          name="width"
                          value={formData.width}
                          onChange={handleChange}
                          placeholder="80"
                          className="w-full px-3 py-2 text-sm border rounded-lg bg-white border-slate-300 focus:outline-hidden focus:ring-2 focus:ring-sky-600"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate-700 mb-1">Højde (cm)</label>
                        <input
                          type="number"
                          name="height"
                          value={formData.height}
                          onChange={handleChange}
                          placeholder="140"
                          className="w-full px-3 py-2 text-sm border rounded-lg bg-white border-slate-300 focus:outline-hidden focus:ring-2 focus:ring-sky-600"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate-700 mb-1">Vægt (kg)</label>
                        <input
                          type="number"
                          name="weight"
                          value={formData.weight}
                          onChange={handleChange}
                          placeholder="350"
                          className="w-full px-3 py-2 text-sm border rounded-lg bg-white border-slate-300 focus:outline-hidden focus:ring-2 focus:ring-sky-600"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Sektion 3: Parter (Afsender / Modtager / Køber) */}
                <div>
                  <h3 className="text-lg font-bold text-[#0f2b48] pb-2 border-b border-slate-100 flex items-center gap-2 mb-4">
                    <i className="fa-solid fa-users text-sky-600" aria-hidden="true" />
                    <span>Parter & Betalingsforhold</span>
                  </h3>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="p-3 bg-slate-50 rounded-xl border border-slate-200">
                      <p className="text-xs font-bold text-slate-800 mb-2">Afsender *</p>
                      <div className="flex gap-4 text-xs font-medium">
                        <label className="inline-flex items-center gap-1.5 cursor-pointer">
                          <input
                            type="radio"
                            name="senderType"
                            value="Firma"
                            checked={formData.senderType === 'Firma'}
                            onChange={handleChange}
                            className="text-[#0f2b48]"
                          />
                          <span>Firma</span>
                        </label>
                        <label className="inline-flex items-center gap-1.5 cursor-pointer">
                          <input
                            type="radio"
                            name="senderType"
                            value="Privat"
                            checked={formData.senderType === 'Privat'}
                            onChange={handleChange}
                            className="text-[#0f2b48]"
                          />
                          <span>Privat</span>
                        </label>
                      </div>
                      {formData.senderType === 'Firma' && (
                        <div className="mt-2 space-y-1.5">
                          <input
                            type="text"
                            name="senderCompany"
                            value={formData.senderCompany}
                            onChange={handleChange}
                            placeholder="Firmanavn"
                            className="w-full px-2.5 py-1.5 text-xs border rounded-md bg-white border-slate-300"
                          />
                          <input
                            type="text"
                            name="senderVat"
                            value={formData.senderVat}
                            onChange={handleChange}
                            placeholder="CVR / SE-nr"
                            className="w-full px-2.5 py-1.5 text-xs border rounded-md bg-white border-slate-300 font-mono"
                          />
                        </div>
                      )}
                    </div>

                    <div className="p-3 bg-slate-50 rounded-xl border border-slate-200">
                      <p className="text-xs font-bold text-slate-800 mb-2">Modtager *</p>
                      <div className="flex gap-4 text-xs font-medium">
                        <label className="inline-flex items-center gap-1.5 cursor-pointer">
                          <input
                            type="radio"
                            name="receiverType"
                            value="Firma"
                            checked={formData.receiverType === 'Firma'}
                            onChange={handleChange}
                            className="text-[#0f2b48]"
                          />
                          <span>Firma</span>
                        </label>
                        <label className="inline-flex items-center gap-1.5 cursor-pointer">
                          <input
                            type="radio"
                            name="receiverType"
                            value="Privat"
                            checked={formData.receiverType === 'Privat'}
                            onChange={handleChange}
                            className="text-[#0f2b48]"
                          />
                          <span>Privat</span>
                        </label>
                      </div>
                      {formData.receiverType === 'Firma' && (
                        <div className="mt-2 space-y-1.5">
                          <input
                            type="text"
                            name="receiverCompany"
                            value={formData.receiverCompany}
                            onChange={handleChange}
                            placeholder="Firmanavn"
                            className="w-full px-2.5 py-1.5 text-xs border rounded-md bg-white border-slate-300"
                          />
                          <input
                            type="text"
                            name="receiverVat"
                            value={formData.receiverVat}
                            onChange={handleChange}
                            placeholder="CVR / SE-nr"
                            className="w-full px-2.5 py-1.5 text-xs border rounded-md bg-white border-slate-300 font-mono"
                          />
                        </div>
                      )}
                    </div>

                    <div className="p-3 bg-slate-50 rounded-xl border border-slate-200">
                      <p className="text-xs font-bold text-slate-800 mb-2">Køber *</p>
                      <div className="flex gap-4 text-xs font-medium">
                        <label className="inline-flex items-center gap-1.5 cursor-pointer">
                          <input
                            type="radio"
                            name="buyerType"
                            value="Firma"
                            checked={formData.buyerType === 'Firma'}
                            onChange={handleChange}
                            className="text-[#0f2b48]"
                          />
                          <span>Firma</span>
                        </label>
                        <label className="inline-flex items-center gap-1.5 cursor-pointer">
                          <input
                            type="radio"
                            name="buyerType"
                            value="Privat"
                            checked={formData.buyerType === 'Privat'}
                            onChange={handleChange}
                            className="text-[#0f2b48]"
                          />
                          <span>Privat</span>
                        </label>
                      </div>
                      {formData.buyerType === 'Firma' && (
                        <div className="mt-2 space-y-1.5">
                          <input
                            type="text"
                            name="buyerCompany"
                            value={formData.buyerCompany}
                            onChange={handleChange}
                            placeholder="Firmanavn"
                            className="w-full px-2.5 py-1.5 text-xs border rounded-md bg-white border-slate-300"
                          />
                          <input
                            type="text"
                            name="buyerVat"
                            value={formData.buyerVat}
                            onChange={handleChange}
                            placeholder="CVR / SE-nr"
                            className="w-full px-2.5 py-1.5 text-xs border rounded-md bg-white border-slate-300 font-mono"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Sektion 4: Kontaktinformationer */}
                <div>
                  <h3 className="text-lg font-bold text-[#0f2b48] pb-2 border-b border-slate-100 flex items-center gap-2 mb-4">
                    <i className="fa-solid fa-address-book text-sky-600" aria-hidden="true" />
                    <span>Dine Kontaktinformationer</span>
                  </h3>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">Navn *</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Navn"
                        className="w-full px-3 py-2 text-sm border rounded-lg bg-white border-slate-300 focus:outline-hidden focus:ring-2 focus:ring-sky-600"
                      />
                      {errors.name && <p className="text-xs text-red-600 mt-1">{errors.name}</p>}
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">Telefonnummer *</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+45 12 34 56 78"
                        className="w-full px-3 py-2 text-sm border rounded-lg bg-white border-slate-300 focus:outline-hidden focus:ring-2 focus:ring-sky-600"
                      />
                      {errors.phone && <p className="text-xs text-red-600 mt-1">{errors.phone}</p>}
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">E-mail *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="navn@virksomhed.dk"
                        className="w-full px-3 py-2 text-sm border rounded-lg bg-white border-slate-300 focus:outline-hidden focus:ring-2 focus:ring-sky-600"
                      />
                      {errors.email && <p className="text-xs text-red-600 mt-1">{errors.email}</p>}
                    </div>
                  </div>

                  <div className="mt-4">
                    <label className="block text-xs font-semibold text-slate-700 mb-1">
                      Krav og / eller kommentarer (særlige ønsker, timing, emballage, liftkrav etc.)
                    </label>
                    <textarea
                      name="comments"
                      rows={3}
                      value={formData.comments}
                      onChange={handleChange}
                      placeholder="Skriv eventuelle supplerende oplysninger..."
                      className="w-full px-3 py-2 text-sm border rounded-lg bg-white border-slate-300 focus:outline-hidden focus:ring-2 focus:ring-sky-600"
                    />
                  </div>
                </div>

                <div className="pt-4 border-t border-slate-200 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
                  <p className="text-xs text-slate-500">
                    Ved indsendelse modtager Fastlane dine specifikationer og udarbejder et uforpligtende tilbud.
                  </p>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-3.5 rounded-lg bg-[#b25d19] hover:bg-[#974d12] text-white font-bold text-sm shadow-sm hover:shadow-md transition-all active:scale-98 disabled:opacity-70 cursor-pointer"
                  >
                    {isSubmitting ? (
                      <>
                        <i className="fa-solid fa-spinner fa-spin mr-2" aria-hidden="true" />
                        <span>Sender forespørgsel...</span>
                      </>
                    ) : (
                      <>
                        <i className="fa-solid fa-paper-plane mr-2" aria-hidden="true" />
                        <span>Indsend Fragtforespørgsel</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
